<script lang="ts">
import TowerCard from '@/views/tower_visualizer/TowerCard.vue';
import { ProgramTower } from '@/types/tower/v2/Tower';
import DeleteTowerModal from '@/views/tower_dashboard/DeleteTowerModal.vue';
import MultiTowerSaveModal from './MultiTowerSaveModal.vue';
import { SaveTowerRequest } from '@/services/v2/TowerService';
import { TowerType } from '@/views/tower_dashboard/types/tower-dashboard';
import { NEW_COVERAGE_TOOLTIP } from '@/views/tower_dashboard/types/tower-dashboard';
import { BOUND_COVERAGE_TOOLTIP } from '@/views/tower_dashboard/types/tower-dashboard';
import { RENEWAL_TOOLTIP } from '@/views/tower_dashboard/types/tower-dashboard';
import { ChevronDown, Plus } from '@lucide/vue';
import CreateTowerModal from '../tower_dashboard/CreateTowerModal.vue';
export default {
  name: 'TowerVisualizerDashboard',
  components: {
    TowerCard,
    DeleteTowerModal,
    MultiTowerSaveModal,
  },
};
</script>

<script setup lang="ts">
import { onMounted, ref, watch, computed, useAttrs, reactive } from 'vue';
import { GetTowerCardsParams, GetTowersParams, towerService } from '@/services/v2/TowerService';
import { TowerCardData, TowerTypeData } from '@/views/tower_dashboard/types/tower-dashboard';
import { useRouter } from 'vue-router';
import { Commit } from '@/store';
import EditTowerModal from '@/views/tower_dashboard/EditTowerModal.vue';
import { StoreMutationType } from '@/types/StoreMutationType';
import { IsUserAClient } from '@/utilities/AuthUtils';
import { AppState } from '@/store';
import { useStore } from 'vuex';
import { escapeRegex } from '@/utilities/tower/regex';
import Toggle from '@/components/v2/Toggle.vue';

const attrs = useAttrs();

const store = useStore<AppState>();

// ---------------
// reactive data
// ---------------
const router = useRouter();
const towerCards = ref<TowerCardData[]>([]);
const towerCardsByYear = ref<[number, TowerCardData[]][]>([]);
const selectedCardIds = ref<string[]>([]);
const loading = ref(true);
const showCreateMultiTowerModal = ref(false);
const showRenameTowerModal = ref(false);
const deleteTowerState = ref<{
  show: boolean;
  highlightTowerId?: string;
  towerCard?: TowerCardData;
}>({ show: false, highlightTowerId: undefined, towerCard: undefined });
const searchFilter = ref('');
const searchTimeout = ref<NodeJS.Timeout>();
const towerTypes = ref<TowerTypeData[]>();
const editTowerId = ref<string>();
const disableTowers = ref<false>();
const selectTowerCreateTypeList = ref<HTMLElement | null>(null);
const selectedCreateType = ref<'create' | 'createAndSave'>('create');
const showCurrentOnly = ref(false);

const newTowerModalData = reactive({
  show: false,
  type: TowerType.BoundCoverage,
});

// ---------
// computed
// ---------
const createTowerButtonText = computed(() => {
  if (selectedCreateType.value === 'create') {
    return 'View Towers';
  } else {
    return 'Create Tower Visualization';
  }
});

// -----------
// lifecycles
// -----------
onMounted(async () => {
  loading.value = true;
  await fetchTowerCards();
  towerTypes.value = await towerService.getTowerTypes();
  loading.value = false;
});

// ---------
// watchers
// ---------
watch(searchFilter, () => {
  if (loading.value) {
    return;
  }
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value);
  }
  searchTimeout.value = setTimeout(() => {
    fetchTowerCards();
  }, 400);
});

watch(showCurrentOnly, () => {
  if (loading.value) {
    return;
  }
  fetchTowerCards();
});

// ---------
// computed
// ---------
const isFiltered = computed(() => {
  return !!searchFilter.value;
});

const isUserAClient = computed(() => {
  return IsUserAClient(store.state.user);
});

// --------
// methods
// --------
async function fetchTowerCards() {
  const params: GetTowerCardsParams = {
    searchQuery: searchFilter.value,
    accountId: Number(attrs.accountId as string),
    showCurrentOnly: showCurrentOnly.value,
  };

  towerCards.value = await towerService.getTowerCards(params);
  if (isUserAClient.value) {
    towerCards.value = towerCards.value.filter((tower) => tower.published).map((tower) => ({ ...tower, contributors: '' }));
  }

  for (const towerCard of towerCards.value) {
    if (towerCard.towerTypeId === TowerType.MultiTower) {
      towerCard.towerCards = [];
      const tower = (await towerService.getTowers({ towerIds: [towerCard.towerId] }))[0];
      if (tower) {
        const towerObject = JSON.parse(tower.towerObject);
        for (const object of towerObject.objects) {
          if (object['towerCard']) {
            towerCard.towerCards.push(object['towerCard']);
          }
        }
      }
    }
  }

  const yearMap = towerCards.value.reduce((acc, card) => {
    let year = card.program.year;

    // multi-tower -> get the max year from towerCards

    if (!year && card.towerCards) {
      const years = card.towerCards.map((t) => t.program.year).filter((y) => y !== undefined) as number[];

      if (years.length > 0) {
        year = Math.max(...years);

        // set year and coverage type for multi-tower on the program for sorting purposes
        card.program.year = year;

        const coverageTypes = card.towerCards?.map((t) => t.program.coverageTypes).filter((ct) => ct);
        if (coverageTypes?.length > 0) {
          // grab the first coverage type
          card.program.coverageTypes = coverageTypes[0];
        }
      }
    }

    if (acc.has(year)) {
      acc.get(year)?.push(card);
    } else {
      acc.set(year, [card]);
    }

    return acc;
  }, new Map<number, TowerCardData[]>());

  // sort by year descending
  const entries = Array.from(yearMap.entries()).sort((a, b) => b[0] - a[0]);

  entries.forEach(([year, cards]) => {
    cards.sort((a, b) => {
      if (a.program && b.program) {
        return (a.program.coverageTypes || '').localeCompare(b.program.coverageTypes || '');
      }
      return 0;
    });
  });

  towerCardsByYear.value = entries;

  loading.value = false;
}

function handleTowerSelect(towerId: string, selected: boolean) {
  if (selected) {
    selectedCardIds.value.push(towerId);
  } else {
    selectedCardIds.value = selectedCardIds.value.filter((id) => id !== towerId);
  }
}

function handleEditTower(towerId: string) {
  const towerCard = towerCards.value.find((t) => t.towerId === towerId);
  if (towerCard?.towerTypeId === 5) {
    router.push('towers/' + towerId);
  } else {
    router.push('/v2/tower/' + towerId);
  }
}

async function handleCreateMultiTower(versionLabel: string): Promise<void> {
  try {
    const request = {
      action: 'new',
      tower: {
        versionLabel,
        towerObject: await createSerializedTower(),
        towerTypeId: TowerType.MultiTower,
        accountId: Number(attrs.accountId as string),
      } as ProgramTower,
    } as SaveTowerRequest;

    const savedTower = await towerService.saveTower(request);
    router.push('towers/' + savedTower.towerId);
  } catch (e) {
    console.error(e);
  }
  showCreateMultiTowerModal.value = false;
}

function openRenameTowerModal(towerId: string) {
  const tower = towerCards.value?.filter((t) => t.towerId === towerId)[0];
  if (tower) {
    editTowerId.value = tower.towerId;
    showRenameTowerModal.value = true;
  }
}

function handleRenameTower(success: boolean) {
  if (success) {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Tower updated successfully' });
  } else {
    Commit(StoreMutationType.SET_FLASH_MESSAGE, { text: 'Error: Could not update tower', color: 'error' });
  }

  showRenameTowerModal.value = false;
  fetchTowerCards();
}

async function handleDuplicateTower(towerId: string) {
  // find count of matching tower versions for this tower (by programId and tower type)
  // add 1 to that count to get the new version number
  const tower: ProgramTower = (await towerService.getTowers({ towerIds: [towerId] }))[0];

  if (!tower) {
    return;
  }

  const params = {
    programId: tower.programId ? Number(tower.programId) : 0,
    towerTypeId: tower.towerTypeId,
    towerName: tower.towerName,
  } as GetTowersParams;

  const towers = await towerService.getTowers(params);

  if (!towers || towers.length === 0) {
    return;
  }

  const baseVersionLabel = tower.versionLabel?.replace(/(?: \(\d+\))?$/, '') ?? '';
  const duplicateLabelRegex = new RegExp(`^${escapeRegex(baseVersionLabel)}(?: \\(\\d+\\))?$`);

  const versionLabelCount = towers.reduce((acc, t) => {
    if (t.versionLabel && t.versionLabel.match(duplicateLabelRegex)) {
      acc++;
    }
    return acc;
  }, 0);

  if (versionLabelCount === 0) {
    return;
  } else {
    tower.versionLabel = `${baseVersionLabel} (${versionLabelCount})`;
  }

  await towerService.createTower(tower);
  await fetchTowerCards();
}

function handleConfirmDeleteTower(towerCard: TowerCardData) {
  deleteTowerState.value = {
    show: true,
    highlightTowerId: towerCard.towerId,
    towerCard,
  };
}

async function handleDeleteTower(towerId?: string) {
  if (!towerId) {
    return;
  }

  await towerService.deleteTower(towerId);
  deleteTowerState.value = {
    show: false,
    highlightTowerId: undefined,
    towerCard: undefined,
  };
  await fetchTowerCards();
}

async function handleTogglePublishTower(towerId?: string, published?: boolean) {
  if (!towerId) {
    console.error('No towerId provided');
    return;
  }

  try {
    await towerService.updateTowerInfo(towerId, { published: published ?? false });
  } catch (e) {
    console.error('Error publishing/unpublishing tower', e);
  }

  await fetchTowerCards();
}

function resetFilters() {
  searchFilter.value = '';
}

function handleSelectCreateType(type: 'create' | 'createAndSave') {
  selectedCreateType.value = type;
  selectTowerCreateTypeList.value?.blur();
}

async function handleCreateTowerAction() {
  if (selectedCreateType.value === 'create') {
    router.push({ path: 'towers/new', query: { serializedTower: await createSerializedTower() } });
  } else {
    showCreateMultiTowerModal.value = true;
  }
}

function openNewTowerModal(type: TowerType) {
  newTowerModalData.type = type;
  newTowerModalData.show = true;
}

function handleCreateTower(towerId: string) {
  router.push('/v2/tower/' + towerId);
}

async function createSerializedTower(): Promise<string> {
  try {
    const tempCanvas = new fabric.StaticCanvas(null, {});
    const canvas = new fabric.StaticCanvas(null, {});

    const towers = await towerService.getTowers({ towerIds: selectedCardIds.value });

    for (const tower of towers) {
      const towerObject = JSON.parse(tower.towerObject);
      tempCanvas.loadFromJSON(towerObject, () => {
        tempCanvas.renderAll();

        const group = new fabric.GroupBase(tempCanvas.getObjects(), {} as fabric.GroupBaseOptions);
        group.set({
          id: tower.towerId,
          towerCard: towerCards.value.find((t) => t.towerId === tower.towerId),
          scaleX: 0.7,
          scaleY: 0.7,
        } as fabric.GroupBaseOptions);
        canvas.add(group);
        group.centerH();
        group.centerV();
        canvas.renderAll();
        tempCanvas.clear();
      });
    }

    return JSON.stringify(canvas.toJSON());
  } catch (e) {
    console.error(e);
    return '';
  }
}
</script>

<template>
  <div class="mt-6 flex h-full flex-col">
    <!-- Overlay to disable editing while card options are open -->
    <div
      v-if="disableTowers"
      @click="$event.stopPropagation()"
      class="pointer-events-all absolute z-10 h-full w-full cursor-auto"></div>

    <!-- options -->
    <div class="z-100 flex w-10/12 flex-row items-center justify-center gap-6 self-center">
      <!-- Search bar -->
      <input
        type="text"
        placeholder="Search"
        v-model="searchFilter"
        class="input w-64 border-solid shadow-xs" />

      <button
        v-if="isFiltered"
        @click="resetFilters"
        :disabled="!isFiltered"
        class="h-fit p-0 text-xs font-bold uppercase transition-all duration-100"
        :class="isFiltered ? 'bg-white text-red-500 hover:scale-110' : 'bg-red-50 text-gray-400'">
        Reset
      </button>
      <div class="join">
        <button
          class="btn join-item btn-primary"
          :class="{ 'btn-disabled': selectedCardIds.length === 0 }"
          @click="handleCreateTowerAction"
          data-testid="view-tower-button">
          {{ createTowerButtonText }}
        </button>
        <div
          v-if="!isUserAClient"
          class="dropdown dropdown-left dropdown-bottom">
          <div
            tabindex="0"
            role="button"
            class="btn join-item p-1">
            <ChevronDown :size="20" />
          </div>
          <ul
            ref="selectTowerCreateTypeList"
            tabindex="0"
            class="dropdown-content w-fit-content menu z-1 flex-nowrap bg-base-100 shadow">
            <li>
              <a
                class="w-44 text-black"
                @click="handleSelectCreateType('create')"
                >View Towers</a
              >
            </li>
            <li>
              <a
                class="w-44 text-black"
                @click="handleSelectCreateType('createAndSave')"
                >Create Tower Visualization</a
              >
            </li>
          </ul>
        </div>
      </div>
      <Toggle
        id="toggle-current-only"
        v-model="showCurrentOnly"
        label="Show Current Only"
        variant="secondary"
        size="md" />
    </div>

    <!-- Tower creation cards -->
    <div
      v-if="!isUserAClient"
      class="mt-4 flex flex-row flex-wrap items-center justify-center gap-x-6 self-center">
      <!-- bound coverage card -->
      <div
        @click="openNewTowerModal(TowerType.BoundCoverage)"
        class="group tooltip pointer-events-auto tooltip-bottom z-30 flex cursor-pointer justify-items-center"
        :data-tip="BOUND_COVERAGE_TOOLTIP">
        <div
          class="m-auto flex flex-col justify-center rounded-lg border border-solid border-blue-200 bg-white px-2 text-[#133044] shadow-md transition-all duration-150 group-hover:border-blue-200 group-hover:bg-blue-50 group-hover:shadow-none">
          <div class="flex flex-row items-center gap-2 self-center p-2">
            <Plus class="text-gray-400" />
            <h2 class="text-center text-sm font-medium">Bound Coverage</h2>
          </div>
        </div>
      </div>

      <!-- new coverage card -->
      <div
        @click="openNewTowerModal(TowerType.NewCoverage)"
        class="group tooltip pointer-events-auto tooltip-bottom z-30 flex cursor-pointer justify-items-center"
        :data-tip="NEW_COVERAGE_TOOLTIP">
        <div
          class="m-auto flex flex-row justify-center rounded-lg border border-solid border-orange-200 bg-white px-2 text-[#133044] shadow-md transition-all duration-150 group-hover:border-blue-200 group-hover:bg-blue-50 group-hover:shadow-none">
          <div class="flex flex-row items-center gap-2 self-center p-2">
            <Plus class="text-gray-400" />
            <h2 class="text-center text-sm font-medium">New Coverage</h2>
          </div>
        </div>
      </div>

      <!-- new renewal card -->
      <div
        @click="openNewTowerModal(TowerType.Renewal)"
        class="group tooltip pointer-events-auto tooltip-bottom z-20 flex cursor-pointer justify-items-center"
        :data-tip="RENEWAL_TOOLTIP">
        <div
          class="m-auto flex flex-col justify-center rounded-lg border border-solid border-red-200 bg-white px-2 text-[#133044] shadow-md transition-all duration-150 group-hover:border-blue-200 group-hover:bg-blue-50 group-hover:shadow-none">
          <div class="flex flex-row items-center gap-2 self-center p-2">
            <Plus class="text-gray-400" />
            <span class="text-center text-sm font-medium">Renewal</span>
          </div>
        </div>
      </div>
    </div>

    <!--  card container -->
    <div
      v-if="loading"
      class="mt-10 flex items-center justify-center">
      <span class="loading loading-lg loading-dots text-gray-600"></span>
    </div>
    <div class="mt-5 mb-10 flex w-10/12 flex-row flex-wrap items-center justify-start gap-x-8 gap-y-5 self-center">
      <template
        v-for="[year, cards] in towerCardsByYear"
        :key="year">
        <div class="mt-2 w-full">
          <div class="w-full text-lg font-bold">
            {{ year }}
          </div>

          <div class="divider m-0 mx-auto w-full"></div>
        </div>

        <TowerCard
          v-for="(card, index) in cards"
          :key="index"
          :card-data="card"
          @click="handleTowerSelect"
          @rename="openRenameTowerModal"
          @edit="handleEditTower"
          @duplicate="handleDuplicateTower"
          @delete="handleConfirmDeleteTower"
          @toggle-publish="handleTogglePublishTower"
          :highlightTowerId="deleteTowerState.highlightTowerId"
          v-model:options-open="disableTowers"
          data-testid="tower-card" />
      </template>
    </div>

    <EditTowerModal
      v-if="showRenameTowerModal"
      :tower-id="editTowerId"
      @close="showRenameTowerModal = false"
      @update="handleRenameTower" />
    <DeleteTowerModal
      :show="deleteTowerState.show"
      :tower-card="deleteTowerState.towerCard"
      :handle-cancel="() => (deleteTowerState = { show: false, towerCard: undefined })"
      :handle-action="(towerId: string | undefined) => handleDeleteTower(towerId)" />
    <MultiTowerSaveModal
      :show="showCreateMultiTowerModal"
      @cancel="showCreateMultiTowerModal = false"
      @save="handleCreateMultiTower" />
    <CreateTowerModal
      :show="newTowerModalData.show"
      :tower-type="newTowerModalData.type"
      :account-id="String(attrs.accountId)"
      @close="newTowerModalData.show = false"
      @create="handleCreateTower" />
  </div>
</template>
